import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DigitalTwin = ({ user, onBack }) => {
  const [currentView, setCurrentView] = useState('create'); // 'create', 'world', 'progress'
  const [avatar, setAvatar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [avatarCustomization, setAvatarCustomization] = useState({
    skinTone: '#F4C2A6',
    hairStyle: 'short',
    hairColor: '#2C1810',
    eyeColor: '#4A7C59',
    outfit: 'casual',
    accessories: []
  });
  const [badges, setBadges] = useState([]);
  const [taskProgress, setTaskProgress] = useState([]);

  useEffect(() => {
    loadDigitalTwin();
    loadProgress();
  }, []);

  const loadDigitalTwin = async () => {
    try {
      const response = await axios.get(`${API}/digital-twin`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.avatar) {
        setAvatar(response.data.avatar);
        setAvatarCustomization(response.data.avatar.customization);
      }
    } catch (error) {
      console.log('No existing digital twin found');
    }
  };

  const loadProgress = async () => {
    try {
      const response = await axios.get(`${API}/digital-twin/progress`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setBadges(response.data.badges || []);
      setTaskProgress(response.data.tasks || []);
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  const saveAvatar = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/digital-twin/avatar`, {
        customization: avatarCustomization
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAvatar(response.data.avatar);
      alert('Digital Twin created successfully!');
      setCurrentView('world');
    } catch (error) {
      alert('Failed to save avatar');
    } finally {
      setLoading(false);
    }
  };

  const skinTones = ['#F4C2A6', '#E8B896', '#D4A574', '#C68642', '#8D5524', '#4A2511'];
  const hairStyles = ['short', 'medium', 'long', 'curly', 'bald'];
  const hairColors = ['#2C1810', '#8B4513', '#D2691E', '#FFD700', '#FF0000', '#000000'];
  const eyeColors = ['#4A7C59', '#1E90FF', '#8B4513', '#228B22', '#808080'];
  const outfits = ['casual', 'formal', 'sporty', 'professional', 'traditional'];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">🎮 Digital Twin</h1>
          <p className="text-gray-600 mt-2">Create your digital self and practice real-world scenarios</p>
        </div>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setCurrentView('create')}
          className={`px-6 py-3 font-medium transition-all ${
            currentView === 'create'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          👤 Create Avatar
        </button>
        <button
          onClick={() => setCurrentView('world')}
          className={`px-6 py-3 font-medium transition-all ${
            currentView === 'world'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-blue-600'
          }`}
          disabled={!avatar}
        >
          🌍 3D World
        </button>
        <button
          onClick={() => setCurrentView('progress')}
          className={`px-6 py-3 font-medium transition-all ${
            currentView === 'progress'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-blue-600'
          }`}
        >
          🏆 Progress
        </button>
      </div>

      {/* Create Avatar View */}
      {currentView === 'create' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Avatar Preview */}
          <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl p-8 flex flex-col items-center justify-center">
            <h3 className="text-xl font-bold mb-6">Avatar Preview</h3>
            <div className="relative w-64 h-64 bg-white rounded-full shadow-lg flex items-center justify-center overflow-hidden">
              {/* Simple SVG Avatar Preview */}
              <svg width="200" height="200" viewBox="0 0 200 200">
                {/* Head */}
                <circle cx="100" cy="80" r="40" fill={avatarCustomization.skinTone} />
                
                {/* Hair */}
                {avatarCustomization.hairStyle !== 'bald' && (
                  <>
                    <ellipse cx="100" cy="60" rx="45" ry="30" fill={avatarCustomization.hairColor} />
                    {avatarCustomization.hairStyle === 'long' && (
                      <>
                        <rect x="60" y="85" width="20" height="40" fill={avatarCustomization.hairColor} rx="10" />
                        <rect x="120" y="85" width="20" height="40" fill={avatarCustomization.hairColor} rx="10" />
                      </>
                    )}
                  </>
                )}
                
                {/* Eyes */}
                <circle cx="88" cy="75" r="5" fill={avatarCustomization.eyeColor} />
                <circle cx="112" cy="75" r="5" fill={avatarCustomization.eyeColor} />
                
                {/* Mouth */}
                <path d="M 90 95 Q 100 100 110 95" stroke={avatarCustomization.skinTone} strokeWidth="2" fill="none" />
                
                {/* Body */}
                <rect x="70" y="120" width="60" height="70" fill={
                  avatarCustomization.outfit === 'formal' ? '#1a1a1a' :
                  avatarCustomization.outfit === 'sporty' ? '#ff6b6b' :
                  avatarCustomization.outfit === 'professional' ? '#2c5aa0' :
                  avatarCustomization.outfit === 'traditional' ? '#8b4513' :
                  '#4ecdc4'
                } rx="10" />
              </svg>
            </div>
            <p className="mt-4 text-gray-700 text-center">
              Customize your avatar using the options on the right
            </p>
          </div>

          {/* Customization Options */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">🎨 Skin Tone</h3>
              <div className="grid grid-cols-6 gap-3">
                {skinTones.map((tone) => (
                  <button
                    key={tone}
                    onClick={() => setAvatarCustomization({...avatarCustomization, skinTone: tone})}
                    className={`w-12 h-12 rounded-full border-4 transition-all ${
                      avatarCustomization.skinTone === tone ? 'border-blue-600 scale-110' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: tone }}
                  />
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">💇 Hair Style</h3>
              <div className="grid grid-cols-3 gap-3">
                {hairStyles.map((style) => (
                  <button
                    key={style}
                    onClick={() => setAvatarCustomization({...avatarCustomization, hairStyle: style})}
                    className={`px-4 py-3 rounded-lg font-medium capitalize transition-all ${
                      avatarCustomization.hairStyle === style
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {style}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">🎨 Hair Color</h3>
              <div className="grid grid-cols-6 gap-3">
                {hairColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setAvatarCustomization({...avatarCustomization, hairColor: color})}
                    className={`w-12 h-12 rounded-full border-4 transition-all ${
                      avatarCustomization.hairColor === color ? 'border-blue-600 scale-110' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">👁️ Eye Color</h3>
              <div className="grid grid-cols-5 gap-3">
                {eyeColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setAvatarCustomization({...avatarCustomization, eyeColor: color})}
                    className={`w-12 h-12 rounded-full border-4 transition-all ${
                      avatarCustomization.eyeColor === color ? 'border-blue-600 scale-110' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">👔 Outfit</h3>
              <div className="grid grid-cols-3 gap-3">
                {outfits.map((outfit) => (
                  <button
                    key={outfit}
                    onClick={() => setAvatarCustomization({...avatarCustomization, outfit: outfit})}
                    className={`px-4 py-3 rounded-lg font-medium capitalize transition-all ${
                      avatarCustomization.outfit === outfit
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {outfit}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={saveAvatar}
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50"
            >
              {loading ? 'Creating...' : avatar ? '💾 Update Avatar' : '✨ Create Digital Twin'}
            </button>
          </div>
        </div>
      )}

      {/* 3D World View */}
      {currentView === 'world' && (
        <ThreeDWorld avatar={avatar} user={user} onTaskComplete={loadProgress} />
      )}

      {/* Progress View */}
      {currentView === 'progress' && (
        <ProgressDashboard badges={badges} taskProgress={taskProgress} />
      )}
    </div>
  );
};

// 3D World Component
const ThreeDWorld = ({ avatar, user, onTaskComplete }) => {
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [activeTask, setActiveTask] = useState(null);

  const scenarios = [
    {
      id: 'job_interview',
      title: 'Job Interview Practice',
      icon: '💼',
      description: 'Practice professional interviews in realistic office settings',
      tasks: [
        { id: 1, name: 'Retail Position Interview', difficulty: 'Beginner' },
        { id: 2, name: 'Office Manager Interview', difficulty: 'Intermediate' },
        { id: 3, name: 'Tech Company Interview', difficulty: 'Advanced' }
      ]
    },
    {
      id: 'financial_decisions',
      title: 'Financial Management',
      icon: '💰',
      description: 'Learn budgeting, banking, and financial planning',
      tasks: [
        { id: 4, name: 'Opening a Bank Account', difficulty: 'Beginner' },
        { id: 5, name: 'Creating a Monthly Budget', difficulty: 'Intermediate' },
        { id: 6, name: 'Investment Planning', difficulty: 'Advanced' }
      ]
    },
    {
      id: 'social_scenarios',
      title: 'Social Interactions',
      icon: '🤝',
      description: 'Navigate cultural and social situations confidently',
      tasks: [
        { id: 7, name: 'Making New Friends', difficulty: 'Beginner' },
        { id: 8, name: 'Workplace Communication', difficulty: 'Intermediate' },
        { id: 9, name: 'Conflict Resolution', difficulty: 'Advanced' }
      ]
    },
    {
      id: 'healthcare',
      title: 'Healthcare Navigation',
      icon: '🏥',
      description: 'Understand and navigate the healthcare system',
      tasks: [
        { id: 10, name: 'Booking a Doctor Appointment', difficulty: 'Beginner' },
        { id: 11, name: 'Understanding Insurance', difficulty: 'Intermediate' },
        { id: 12, name: 'Emergency Room Visit', difficulty: 'Advanced' }
      ]
    }
  ];

  const startTask = async (task) => {
    setActiveTask(task);
  };

  const completeTask = async (taskId, score) => {
    try {
      await axios.post(`${API}/digital-twin/complete-task`, {
        task_id: taskId,
        score: score
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      onTaskComplete();
      setActiveTask(null);
      alert(`Task completed! Score: ${score}/100`);
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  if (activeTask) {
    return (
      <ThreeDTaskView 
        task={activeTask} 
        avatar={avatar} 
        onComplete={(score) => completeTask(activeTask.id, score)}
        onBack={() => setActiveTask(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl p-8 text-center">
        <h2 className="text-3xl font-bold mb-4">🌍 Welcome to Your 3D World</h2>
        <p className="text-lg opacity-90">
          Your digital twin is ready to practice real-world tasks. Choose a scenario below!
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {scenarios.map((scenario) => (
          <div
            key={scenario.id}
            className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all cursor-pointer"
            onClick={() => setSelectedScenario(scenario)}
          >
            <div className="text-6xl mb-4 text-center">{scenario.icon}</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{scenario.title}</h3>
            <p className="text-gray-600 mb-4">{scenario.description}</p>
            <div className="space-y-2">
              {scenario.tasks.map((task) => (
                <button
                  key={task.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    startTask(task);
                  }}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-blue-50 rounded-lg transition-all"
                >
                  <span className="font-medium">{task.name}</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    task.difficulty === 'Beginner' ? 'bg-green-100 text-green-700' :
                    task.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {task.difficulty}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// 3D Task View Component
const ThreeDTaskView = ({ task, avatar, onComplete, onBack }) => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [score, setScore] = useState(0);
  const canvasRef = useRef(null);

  const steps = [
    { id: 1, description: 'Understand the scenario and objectives' },
    { id: 2, description: 'Navigate the 3D environment' },
    { id: 3, description: 'Complete the interaction' },
    { id: 4, description: 'Receive feedback and score' }
  ];

  useEffect(() => {
    // Initialize basic 3D visualization
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth;
    const height = canvas.height = canvas.offsetHeight;

    // Draw simple 3D-like scene
    const draw = () => {
      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, '#87CEEB');
      gradient.addColorStop(1, '#98D8C8');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // Draw floor
      ctx.fillStyle = '#D3D3D3';
      ctx.beginPath();
      ctx.moveTo(0, height * 0.7);
      ctx.lineTo(width, height * 0.7);
      ctx.lineTo(width, height);
      ctx.lineTo(0, height);
      ctx.closePath();
      ctx.fill();

      // Draw avatar representation
      ctx.fillStyle = avatar?.customization?.skinTone || '#F4C2A6';
      ctx.beginPath();
      ctx.arc(width / 2, height * 0.5, 40, 0, Math.PI * 2);
      ctx.fill();

      // Add task title
      ctx.fillStyle = '#333';
      ctx.font = 'bold 24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(task.name, width / 2, 40);
    };

    draw();
  }, [avatar, task]);

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setProgress((currentStep / steps.length) * 100);
      setScore(score + 25);
    } else {
      onComplete(score);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
        >
          ← Back to Scenarios
        </button>
        <div className="text-right">
          <p className="text-sm text-gray-600">Current Score</p>
          <p className="text-2xl font-bold text-blue-600">{score}/100</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">{task.name}</h2>
        <div className="flex items-center gap-2 mb-4">
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
            task.difficulty === 'Beginner' ? 'bg-green-100 text-green-700' :
            task.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
            'bg-red-100 text-red-700'
          }`}>
            {task.difficulty}
          </span>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-600 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* 3D Canvas */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <canvas
          ref={canvasRef}
          className="w-full"
          style={{ height: '400px' }}
        />
      </div>

      {/* Current Step */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold mb-4">Step {currentStep} of {steps.length}</h3>
        <p className="text-gray-700 mb-6">{steps[currentStep - 1]?.description}</p>
        
        <button
          onClick={handleNext}
          className="w-full py-4 bg-gradient-to-r from-green-500 to-blue-500 text-white font-bold rounded-xl hover:from-green-600 hover:to-blue-600 transition-all"
        >
          {currentStep < steps.length ? 'Continue →' : '✅ Complete Task'}
        </button>
      </div>
    </div>
  );
};

// Progress Dashboard Component
const ProgressDashboard = ({ badges, taskProgress }) => {
  const availableBadges = [
    { id: 'first_task', name: 'First Steps', icon: '🌟', description: 'Complete your first task' },
    { id: 'job_master', name: 'Interview Pro', icon: '💼', description: 'Complete all job interview tasks' },
    { id: 'money_wise', name: 'Financial Guru', icon: '💰', description: 'Complete all financial tasks' },
    { id: 'social_butterfly', name: 'Social Expert', icon: '🤝', description: 'Complete all social tasks' },
    { id: 'health_aware', name: 'Healthcare Navigator', icon: '🏥', description: 'Complete all healthcare tasks' },
    { id: 'perfect_score', name: 'Perfectionist', icon: '⭐', description: 'Score 100/100 on any task' }
  ];

  const totalTasks = taskProgress.length;
  const completedTasks = taskProgress.filter(t => t.completed).length;
  const averageScore = totalTasks > 0 
    ? Math.round(taskProgress.reduce((acc, t) => acc + (t.score || 0), 0) / totalTasks) 
    : 0;

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-purple-500 text-white rounded-xl p-6 shadow-lg">
          <div className="text-5xl mb-2">📊</div>
          <p className="text-sm opacity-90">Tasks Completed</p>
          <p className="text-4xl font-bold">{completedTasks}/{totalTasks}</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-teal-500 text-white rounded-xl p-6 shadow-lg">
          <div className="text-5xl mb-2">🎯</div>
          <p className="text-sm opacity-90">Average Score</p>
          <p className="text-4xl font-bold">{averageScore}%</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-orange-500 text-white rounded-xl p-6 shadow-lg">
          <div className="text-5xl mb-2">🏆</div>
          <p className="text-sm opacity-90">Badges Earned</p>
          <p className="text-4xl font-bold">{badges.length}/{availableBadges.length}</p>
        </div>
      </div>

      {/* Badges */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">🏅 Your Badges</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {availableBadges.map((badge) => {
            const earned = badges.some(b => b.badge_id === badge.id);
            return (
              <div
                key={badge.id}
                className={`p-4 rounded-xl border-2 transition-all ${
                  earned
                    ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-400'
                    : 'bg-gray-50 border-gray-200 opacity-50'
                }`}
              >
                <div className="text-5xl mb-2 text-center">{badge.icon}</div>
                <h3 className="font-bold text-center mb-1">{badge.name}</h3>
                <p className="text-xs text-gray-600 text-center">{badge.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Task History */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">📝 Task History</h2>
        <div className="space-y-3">
          {taskProgress.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No tasks completed yet. Start your first task!</p>
          ) : (
            taskProgress.map((task, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all"
              >
                <div>
                  <p className="font-medium">{task.task_name}</p>
                  <p className="text-sm text-gray-600">{new Date(task.completed_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-600">{task.score}/100</p>
                  {task.score === 100 && <span className="text-xs text-yellow-600">⭐ Perfect!</span>}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default DigitalTwin;
